import { createEffect, createMemo, createSignal } from "solid-js";
import SelectMenuWithSearch from "./SelectMenuWithSearch";
import { flattenOptions } from "../../utils/menu/searchableMenuUtil";
import { isEmpty, isEqual, isNil } from "lodash-es";
import type { ExtendableInputProps } from "../inputs/Input";
import Input from "../inputs/Input";
import type { SelectOption, SelectOptions } from "../../types/Menu";
import MultiSelectTrigger from "./triggers/MultiSelectTrigger";
import { useTranslation } from "../../state/i18n";

type MultiSelectWithSearchProps<T> = {
  id: string;
  ariaLabel: string;
  options: SelectOptions<T>;
  value?: SelectOption<T>["value"][];
  onSelect: (value: SelectOption<T>["value"][]) => void;
  onClose?: () => void;
  width: "matchTrigger" | "240px" | "480px";
  errorMessage?: string;
  placeholder?: string;
};

const MultiSelectWithSearch = <T,>(props: MultiSelectWithSearchProps<T>) => {
  let triggerRef!: HTMLButtonElement | null;
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = createSignal<SelectOption<T>[]>([]);
  const flattenedOptions = createMemo(() => flattenOptions(props.options));

  createEffect(() => {
    if (isEmpty(props.value) || isNil(props.value)) {
      setSelectedOptions([]);
      return;
    }
    const optionMap = new Map(flattenedOptions().map((option) => [JSON.stringify(option.value), option]));
    const newSelected = props.value.map((val) => {
      const found = optionMap.get(JSON.stringify(val));
      if (!isNil(found)) {
        return found;
      }
      return {
        variant: "dynamicValue" as const,
        icon: null,
        value: val,
        label: `${t(($) => $.FIELD_PROPERTY_SUBFORM_UNKNOWN_PLACEHOLDER, { value: String(val) })}`,
        orphaned: true,
      };
    });
    setSelectedOptions(newSelected);
  });

  const handleOptionSelect = (option: SelectOption<T>): void => {
    const exists = selectedOptions().some((o) => isEqual(o.value, option.value));
    const updated = exists
      ? selectedOptions().filter((o) => !isEqual(o.value, option.value))
      : [...selectedOptions(), option];
    setSelectedOptions(updated);
    props.onSelect(updated.map((o) => o.value));
  };

  const handleClear = (option: SelectOption<T>) => {
    setSelectedOptions((prev) => prev.filter((o) => !isEqual(o.value, option.value)));
    props.onSelect(
      selectedOptions()
        .filter((o) => !isEqual(o.value, option.value))
        .map((o) => o.value),
    );

    triggerRef?.focus();
  };

  return (
    <SelectMenuWithSearch
      {...props}
      Trigger={(triggerProps) => (
        <MultiSelectTrigger
          ref={triggerRef!}
          value={selectedOptions()}
          ariaLabel={props.ariaLabel}
          hasError={!isNil(props.errorMessage)}
          onClear={handleClear}
          placeholder={props.placeholder}
          arkProps={triggerProps}
        />
      )}
      flattenedOptions={flattenedOptions()}
      selectedOptions={selectedOptions()}
      onSelect={(option) => handleOptionSelect(option)}
      onClose={() => {
        props.onClose?.();
        triggerRef?.focus();
      }}
      multiple
    />
  );
};

type MultiSelectWithSearchInputProps<T> = Omit<MultiSelectWithSearchProps<T>, "id" | "ariaLabel"> &
  ExtendableInputProps;
export const MultiSelectWithSearchInput = <T,>(props: MultiSelectWithSearchInputProps<T>) => (
  <Input required={props.required} errorMessage={props.errorMessage} info={props.info} label={props.label}>
    {(id) => <MultiSelectWithSearch id={id} ariaLabel={props.label} {...props} />}
  </Input>
);

export default MultiSelectWithSearch;
